import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { useAppSelector, useFormDraft } from "@/shared/hooks";
import { Button, Checkbox, Input, OnboardingTooltip, SearchableSelect, Toast } from "@/shared/ui";
import { useGetCitiesQuery, useGetCitiesByRegionQuery, useGetRegionsQuery } from "@/shared/api/common";
import { formatAddressFromParts, parseAddressString } from "@/shared/utils";
import { hasAnyValue } from "../partialSave";
import AddressesFormSkeleton from "./AddressesFormSkeleton";
import { useGetAddressesQuery, useUpdateAddressesMutation } from "./api";
import { type AddressesFormValues, addressesSchema } from "./validation";

interface RegionOption {
  id: number;
  name: string;
}

interface CityOption {
  id: number;
  name: string;
}

interface Props {
  token: string;
  onNext: () => void;
}

export default function AddressesForm({ token, onNext }: Props) {
  const { t, i18n } = useTranslation("ApplyApplicationPage");
  const isCompleted = useAppSelector((s) => s.completeness.data?.is_complete);

  const { data, isLoading } = useGetAddressesQuery(token);
  const [updateAddresses, { isLoading: isSaving }] = useUpdateAddressesMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const { data: regions = [], isLoading: isRegionsLoading } = useGetRegionsQuery();
  const { data: allCities = [], isLoading: isAllCitiesLoading } = useGetCitiesQuery();

  const [sameAsRegistration, setSameAsRegistration] = useState(false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);

  const methods = useForm<AddressesFormValues>({
    resolver: zodResolver(addressesSchema),
    defaultValues: {
      registration: {
        region_id: undefined as unknown as number,
        region_city_type: "region",
        city_id: undefined,
        street: "",
        home_number: "",
        apartment_number: "",
      },
      factual: {
        region_id: undefined as unknown as number,
        region_city_type: "region",
        city_id: undefined,
        street: "",
        home_number: "",
        apartment_number: "",
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = methods;

  const { applyDraft, hasValidDraft, clearDraft } = useFormDraft<AddressesFormValues>({
    key: `onboarding:addresses:${token}`,
    form: methods,
    schemaVersion: "v1",
  });

  const registrationRegionId = watch("registration.region_id");
  const factualRegionId = watch("factual.region_id");

  const registrationRegion = regions.find((r) => r.id === registrationRegionId);
  const factualRegion = regions.find((r) => r.id === factualRegionId);

  const shouldShowRegistrationCitySelect = registrationRegion?.city_type === "region";
  const shouldShowFactualCitySelect = factualRegion?.city_type === "region";

  const { data: registrationCities = [], isLoading: isRegistrationCitiesLoading } = useGetCitiesByRegionQuery(
    registrationRegionId,
    { skip: !shouldShowRegistrationCitySelect || typeof registrationRegionId !== "number" },
  );

  const { data: factualCities = [], isLoading: isFactualCitiesLoading } = useGetCitiesByRegionQuery(factualRegionId, {
    skip: !shouldShowFactualCitySelect || typeof factualRegionId !== "number",
  });

  useEffect(() => {
    if (!registrationRegionId) return;
    if (registrationRegion?.city_type === "national_city") {
      setValue("registration.city_id", undefined, { shouldValidate: true });
    }
  }, [registrationRegionId, registrationRegion?.city_type, setValue]);

  useEffect(() => {
    if (!factualRegionId) return;
    if (factualRegion?.city_type === "national_city") {
      setValue("factual.city_id", undefined, { shouldValidate: true });
    }
  }, [factualRegionId, factualRegion?.city_type, setValue]);

  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (!data || regions.length === 0 || dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    const regParsed = parseAddressString(data.address_registration ?? "");
    const factParsed = parseAddressString(data.address_factual ?? "");

    reset({
      registration: {
        region_id: undefined as unknown as number,
        region_city_type: "region",
        city_id: undefined,
        street: regParsed?.street ?? "",
        home_number: regParsed?.home_number ?? "",
        apartment_number: regParsed?.apartment_number ?? "",
      },
      factual: {
        region_id: undefined as unknown as number,
        region_city_type: "region",
        city_id: undefined,
        street: factParsed?.street ?? "",
        home_number: factParsed?.home_number ?? "",
        apartment_number: factParsed?.apartment_number ?? "",
      },
    });

    if (regParsed?.city && regions.length > 0) {
      const normalized = regParsed.city.trim().toLowerCase();
      const national = regions.find(
        (r) =>
          r.city_type === "national_city" &&
          [r.name_ru, r.name_kk, r.name_en].some((n) => (n ?? "").trim().toLowerCase() === normalized),
      );

      if (national) {
        setValue("registration.region_id", national.id, { shouldValidate: true });
        setValue("registration.region_city_type", "national_city", { shouldValidate: true });
        setValue("registration.city_id", undefined, { shouldValidate: true });
      } else if (allCities.length > 0) {
        const city = allCities.find((c) =>
          [c.name_ru, c.name_kk, c.name_en].filter(Boolean).some((n) => (n ?? "").trim().toLowerCase() === normalized),
        );
        if (city?.parent_region) {
          setValue("registration.region_id", city.parent_region, { shouldValidate: true });
          setValue("registration.region_city_type", "region", { shouldValidate: true });
          setValue("registration.city_id", city.id, { shouldValidate: true });
        }
      }
    }

    if (factParsed?.city && regions.length > 0) {
      const normalized = factParsed.city.trim().toLowerCase();
      const national = regions.find(
        (r) =>
          r.city_type === "national_city" &&
          [r.name_ru, r.name_kk, r.name_en].some((n) => (n ?? "").trim().toLowerCase() === normalized),
      );

      if (national) {
        setValue("factual.region_id", national.id, { shouldValidate: true });
        setValue("factual.region_city_type", "national_city", { shouldValidate: true });
        setValue("factual.city_id", undefined, { shouldValidate: true });
      } else if (allCities.length > 0) {
        const city = allCities.find((c) =>
          [c.name_ru, c.name_kk, c.name_en].filter(Boolean).some((n) => (n ?? "").trim().toLowerCase() === normalized),
        );
        if (city?.parent_region) {
          setValue("factual.region_id", city.parent_region, { shouldValidate: true });
          setValue("factual.region_city_type", "region", { shouldValidate: true });
          setValue("factual.city_id", city.id, { shouldValidate: true });
        }
      }
    }

    if (hasValidDraft) applyDraft();
  }, [data, reset, setValue, regions, allCities, hasValidDraft, applyDraft]);

  function buildAddressStrings(values: AddressesFormValues) {
    const locale = (i18n.language as "ru" | "kk" | "en") || "ru";

    const regRegion = regions.find((r) => r.id === values.registration.region_id);
    const factRegion = regions.find((r) => r.id === values.factual.region_id);

    const regCity =
      regRegion?.city_type === "national_city"
        ? regRegion
        : registrationCities.find((c) => c.id === values.registration.city_id);
    const factCity =
      factRegion?.city_type === "national_city"
        ? factRegion
        : factualCities.find((c) => c.id === values.factual.city_id);

    function cityName(city: { name_ru: string; name_kk: string; name_en: string } | undefined) {
      if (!city) return "";
      return locale === "kk" ? city.name_kk : locale === "en" ? city.name_en : city.name_ru;
    }

    return {
      address_registration: formatAddressFromParts({
        city: cityName(regCity),
        street: values.registration.street,
        home_number: values.registration.home_number,
        apartment_number: values.registration.apartment_number,
      }),
      address_factual: formatAddressFromParts({
        city: cityName(factCity),
        street: values.factual.street,
        home_number: values.factual.home_number,
        apartment_number: values.factual.apartment_number,
      }),
    };
  }

  async function handleSave() {
    if (isSaving) return;
    const values = getValues();
    const { address_registration, address_factual } = buildAddressStrings(values);

    const payload: { address_registration?: string; address_factual?: string } = {};
    if (address_registration) payload.address_registration = address_registration;
    if (address_factual) payload.address_factual = address_factual;

    if (!hasAnyValue(payload)) return;

    try {
      setIsSavingDraft(true);
      await updateAddresses({ token, body: payload }).unwrap();
      setToast({ color: "positive", text: "addresses.toast.success" });
    } catch {
      setToast({ color: "negative", text: "addresses.toast.error" });
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function onSubmit(values: AddressesFormValues) {
    if (isSaving) return;

    try {
      const locale = (i18n.language as "ru" | "kk" | "en") || "ru";

      const regRegion = regions.find((r) => r.id === values.registration.region_id);
      const factRegion = regions.find((r) => r.id === values.factual.region_id);

      const regCity =
        regRegion?.city_type === "national_city"
          ? regRegion
          : registrationCities.find((c) => c.id === values.registration.city_id);
      const factCity =
        factRegion?.city_type === "national_city"
          ? factRegion
          : factualCities.find((c) => c.id === values.factual.city_id);

      const address_registration = formatAddressFromParts({
        city: regCity ? (locale === "kk" ? regCity.name_kk : locale === "en" ? regCity.name_en : regCity.name_ru) : "",
        street: values.registration.street,
        home_number: values.registration.home_number,
        apartment_number: values.registration.apartment_number,
      });

      const address_factual = formatAddressFromParts({
        city: factCity
          ? locale === "kk"
            ? factCity.name_kk
            : locale === "en"
              ? factCity.name_en
              : factCity.name_ru
          : "",
        street: values.factual.street,
        home_number: values.factual.home_number,
        apartment_number: values.factual.apartment_number,
      });

      await updateAddresses({
        token,
        body: {
          address_registration,
          address_factual,
        },
      }).unwrap();

      clearDraft();
      setToast({
        color: "positive",
        text: "addresses.toast.success",
      });
      onNext();
    } catch {
      setToast({
        color: "negative",
        text: "addresses.toast.error",
      });
    }
  }

  if (isLoading && !data) return <AddressesFormSkeleton />;
  if (isRegionsLoading || isAllCitiesLoading) return <AddressesFormSkeleton />;

  function localizedRegionName(r: { name_ru: string; name_kk: string; name_en: string }) {
    return (i18n.language === "kk" ? r.name_kk : i18n.language === "en" ? r.name_en : r.name_ru) ?? r.name_ru;
  }

  const regionSearchOptions: RegionOption[] = regions.map((r) => ({
    id: r.id,
    name: localizedRegionName(r),
  }));

  const registrationCitySearchOptions: CityOption[] = registrationCities.map((c) => ({
    id: c.id,
    name: i18n.language === "kk" ? c.name_kk : i18n.language === "en" ? c.name_en : c.name_ru,
  }));

  const factualCitySearchOptions: CityOption[] = factualCities.map((c) => ({
    id: c.id,
    name: i18n.language === "kk" ? c.name_kk : i18n.language === "en" ? c.name_en : c.name_ru,
  }));

  const selectedRegistrationRegion = regionSearchOptions.find((r) => r.id === registrationRegionId) ?? null;
  const selectedFactualRegion = regionSearchOptions.find((r) => r.id === factualRegionId) ?? null;
  const selectedRegistrationCity = registrationCitySearchOptions.find((c) => c.id === watch("registration.city_id")) ?? null;
  const selectedFactualCity = factualCitySearchOptions.find((c) => c.id === watch("factual.city_id")) ?? null;

  const registrationValues = watch("registration");
  const factualValues = watch("factual");

  function isBlockComplete(block: AddressesFormValues["registration"]) {
    if (!block.region_id) return false;
    if (block.region_city_type === "region" && !block.city_id) return false;
    if (!block.street?.trim()) return false;
    if (!block.home_number?.trim()) return false;
    return true;
  }

  const canSaveDraft = isBlockComplete(registrationValues) || isBlockComplete(factualValues);

  return (
    <form className="flex flex-col justify-between min-h-[520px] gap-7" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-7">
        <h2 className="text-display-2xs content-base-primary">{t("addresses.title")}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-4">
            <p className="text-body-bold-lg content-base-primary">{t("addresses.registration.label")}</p>

            <SearchableSelect<RegionOption>
              label={t("addresses.region.label")}
              hasRequiredLabel
              placeholder={t("addresses.region.placeholder")}
              options={regionSearchOptions}
              value={selectedRegistrationRegion}
              onChange={(opt) => {
                if (!opt) return;
                const region = regions.find((r) => r.id === opt.id);
                setValue("registration.region_id", opt.id, { shouldValidate: true });
                setValue(
                  "registration.region_city_type",
                  region?.city_type === "national_city" ? "national_city" : "region",
                  {
                    shouldValidate: true,
                  },
                );
                setValue("registration.city_id", undefined, { shouldValidate: true });
              }}
              searchKeys={["name"]}
              displayKey="name"
              hideSearchIcon
              error={errors.registration?.region_id?.message ? t(errors.registration.region_id.message) : undefined}
            />

            {shouldShowRegistrationCitySelect && (
              <SearchableSelect<CityOption>
                label={t("addresses.city.label")}
                placeholder={t("addresses.city.placeholder")}
                options={registrationCitySearchOptions}
                value={selectedRegistrationCity}
                onChange={(opt) =>
                  setValue("registration.city_id", opt ? opt.id : undefined, { shouldValidate: true })
                }
                searchKeys={["name"]}
                displayKey="name"
                hideSearchIcon
                disabled={isRegistrationCitiesLoading}
                error={errors.registration?.city_id?.message ? t(errors.registration.city_id.message as string) : undefined}
              />
            )}

            <Input
              label={t("addresses.street.label")}
              hasRequiredLabel
              placeholder={t("addresses.street.placeholder")}
              {...register("registration.street")}
              error={errors.registration?.street?.message ? t(errors.registration.street.message) : undefined}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t("addresses.home_number.label")}
                hasRequiredLabel
                placeholder={t("addresses.home_number.placeholder")}
                {...register("registration.home_number")}
                error={errors.registration?.home_number?.message ? t(errors.registration.home_number.message) : undefined}
              />

              <Input
                label={t("addresses.apartment_number.label")}
                placeholder={t("addresses.apartment_number.placeholder")}
                {...register("registration.apartment_number")}
                error={
                  errors.registration?.apartment_number?.message ? t(errors.registration.apartment_number.message) : undefined
                }
              />
            </div>

            <Checkbox
              state={sameAsRegistration ? "checked" : "unchecked"}
              onChange={() => {
                const next = !sameAsRegistration;
                setSameAsRegistration(next);
                if (next) {
                  const reg = watch("registration");
                  setValue("factual", { ...reg }, { shouldValidate: true });
                } else {
                  setValue("factual", {
                    region_id: undefined as unknown as number,
                    region_city_type: "region",
                    city_id: undefined,
                    street: "",
                    home_number: "",
                    apartment_number: "",
                  }, { shouldValidate: true });
                }
              }}
              label={t("addresses.copy_from_registration")}
            />
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-body-bold-lg content-base-primary">{t("addresses.actual.label")}</p>

            <SearchableSelect<RegionOption>
              label={t("addresses.region.label")}
              hasRequiredLabel
              placeholder={t("addresses.region.placeholder")}
              options={regionSearchOptions}
              value={selectedFactualRegion}
              onChange={(opt) => {
                if (!opt) return;
                const region = regions.find((r) => r.id === opt.id);
                setValue("factual.region_id", opt.id, { shouldValidate: true });
                setValue(
                  "factual.region_city_type",
                  region?.city_type === "national_city" ? "national_city" : "region",
                  {
                    shouldValidate: true,
                  },
                );
                setValue("factual.city_id", undefined, { shouldValidate: true });
              }}
              searchKeys={["name"]}
              displayKey="name"
              hideSearchIcon
              error={errors.factual?.region_id?.message ? t(errors.factual.region_id.message) : undefined}
            />

            {shouldShowFactualCitySelect && (
              <SearchableSelect<CityOption>
                label={t("addresses.city.label")}
                placeholder={t("addresses.city.placeholder")}
                options={factualCitySearchOptions}
                value={selectedFactualCity}
                onChange={(opt) =>
                  setValue("factual.city_id", opt ? opt.id : undefined, { shouldValidate: true })
                }
                searchKeys={["name"]}
                displayKey="name"
                hideSearchIcon
                disabled={isFactualCitiesLoading}
                error={errors.factual?.city_id?.message ? t(errors.factual.city_id.message as string) : undefined}
              />
            )}

            <Input
              label={t("addresses.street.label")}
              hasRequiredLabel
              placeholder={t("addresses.street.placeholder")}
              {...register("factual.street")}
              error={errors.factual?.street?.message ? t(errors.factual.street.message) : undefined}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t("addresses.home_number.label")}
                hasRequiredLabel
                placeholder={t("addresses.home_number.placeholder")}
                {...register("factual.home_number")}
                error={errors.factual?.home_number?.message ? t(errors.factual.home_number.message) : undefined}
              />

              <Input
                label={t("addresses.apartment_number.label")}
                placeholder={t("addresses.apartment_number.placeholder")}
                {...register("factual.apartment_number")}
                error={errors.factual?.apartment_number?.message ? t(errors.factual.apartment_number.message) : undefined}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {toast && (
          <Toast
            key={`${toast.color}-${toast.text}`}
            color={toast.color}
            text={t(toast.text)}
            onClose={() => setToast(null)}
          />
        )}

        {canSaveDraft ? (
          <Button variant="secondary" size="lg" type="button" onClick={handleSave} disabled={isSaving}>
            {isSavingDraft ? t("addresses.saving") : t("addresses.save")}
          </Button>
        ) : (
          <OnboardingTooltip
            content={<p className="text-body-regular-sm content-static-white">{t("addresses.save_tooltip")}</p>}
            direction="top">
            <Button variant="secondary" size="lg" type="button" disabled>
              {t("addresses.save")}
            </Button>
          </OnboardingTooltip>
        )}

        <Button variant="primary" size="lg" type="submit" disabled={isSaving}>
          {isSaving && !isSavingDraft ? t("addresses.saving") : t("addresses.next_section")}
          {(!isSaving || isSavingDraft) && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
